import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, Input, PageHeader } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";
import { FormArrayOnlyRows } from "components/cck/FormArray";
import NumberInput from "components/cck/NumberInput";

import { Language } from "imports/Utils";

import { gasolineStaffelSchema, GasolineStaffelSchemaInput, GasolineStaffelSchemaOutput } from "./utils/staffel.schema";

import { StaffelInputType } from "__generated__/enums";
import { GetStaffelDocument } from "__generated__/view/staffels/graphql/staffel.queries.generated";
import { SaveStaffelDocument } from "__generated__/view/staffels/graphql/staffel.mutations.generated";

const GasolineStaffelCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [ saveGasolineStaffel, { loading: mutationLoading } ] = useMutation(SaveStaffelDocument);
	const [ getStaffel, { loading: queryLoading } ] = useLazyQuery(GetStaffelDocument);

	const { control, handleSubmit, reset } = useForm<GasolineStaffelSchemaInput, unknown, GasolineStaffelSchemaOutput>({
		resolver: zodResolver(gasolineStaffelSchema),
		defaultValues: {
			id: null,
			description: "",
			staffelItems: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "staffelItems",
	});

	const save = (data: GasolineStaffelSchemaOutput) => {
		saveGasolineStaffel({
			variables: {
				input: {
					type: StaffelInputType.Gasoline,
					gasolineStaffel: {
						id: data.id,
						description: data.description,
						staffelItems: data.staffelItems.map(it => ({
							type: StaffelInputType.Gasoline,
							gasolineStaffelItem: {
								id: it.id ?? 0,
								startPrice: it.startPrice,
								endPrice: it.endPrice,
								surcharge: it.surcharge,
							},
						})),
					},
				},
			},
		}).then(({ data }) => {
			navigate(`/view/staffels/${data?.saveStaffel?.id}/details/gasoline`);
		});
	};

	useEffect(() => {
		if (id) {
			getStaffel({
				variables: {
					id: Number(id),
				},
			}).then((result) => {
				if (result.data?.staffel) {
					reset({
						id: result.data.staffel.id,
						description: result.data.staffel.description ?? "",
						staffelItems: result.data.staffel.staffelItems?.map(it => ({
							id: it.id,
							startPrice: it.__typename === "GasolineStaffelItem" ? it.startPrice : null,
							endPrice: it.__typename === "GasolineStaffelItem" ? it.endPrice : null,
							surcharge: it.__typename === "GasolineStaffelItem" ? it.surcharge : null,
						})) ?? [],
					});
				}
			});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("GASOLINE_STAFFEL")}
					<Helmet title={intl.formatMessage({ id: "GASOLINE_STAFFEL" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(id ? `/view/staffels/${id}/details/gasoline` : "/view/staffels")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={queryLoading || mutationLoading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={6}>
					<Card>
						<FormGroup
							label={intl.formatMessage({ id: "DESCRIPTION" })}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormArrayOnlyRows
							array={fields}
							fields={[ {
								title: intl.formatMessage({ id: "GASOLINE_STAFFEL_START_PRICE" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.startPrice`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "GASOLINE_STAFFEL_END_PRICE" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.endPrice`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							}, {
								title: intl.formatMessage({ id: "GASOLINE_STAFFEL_SURCHARGE" }),
								required: true,
								render: index => (
									<Controller
										name={`staffelItems.${index}.surcharge`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<NumberInput {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								),
							} ]}
							onAdd={() => {
								append({
									id: null,
									startPrice: 0,
									endPrice: 0,
									surcharge: 0,
								});
							}}
							onDelete={remove} />
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default GasolineStaffelCreateEdit;
